export type DocumentMoveSectionKey =
  | "id"
  | "ssn"
  | "paystub"
  | "bank"
  | "other_income"
  | "other";

export type DocumentMoveDestinationKind = "section" | "source" | "id-type" | "trash";

export type DocumentMoveSourceScope = {
  sectionKey: DocumentMoveSectionKey;
  sourceId?: string | number | null;
  sourceIndex?: number | null;
  slot?: "front" | "back" | null;
};

export type MovableDocument = {
  id: string;
  source: DocumentMoveSourceScope;
  documentType?: string | number | null;
  isOfferLetter?: boolean;
  label?: string;
};

export type IdSlotState = {
  idType?: string | number | null;
  frontDocumentId?: string | number | null;
  backDocumentId?: string | number | null;
};

export type DocumentMoveDestination = {
  id: string;
  kind: DocumentMoveDestinationKind;
  sectionKey: DocumentMoveSectionKey | "trash";
  label: string;
  sourceId?: string | number | null;
  sourceIndex?: number | null;
  documentId?: string | number | null;
  idType?: string | number | null;
  idSlot?: "front" | "back" | null;
  currentDocumentCount?: number;
  currentOfferLetterCount?: number;
  acceptsDirectDocuments?: boolean;
  idSlotState?: IdSlotState;
  disabledReason?: string | null;
};

export type DocumentMoveValidationResult = {
  valid: boolean;
  message?: string;
};

const SOURCE_REQUIRED_SECTIONS = new Set(["paystub", "bank", "other_income"]);
const FRONT_ONLY_ID_TYPES = new Set(["1", "10", "11"]);
const TWO_SIDED_ID_TYPES = new Set(["2", "13"]);

const normalize = (value: any) => String(value ?? "").trim();

// Manual/unsaved rows live in the same files arrays as real documents but
// carry a placeholder document id ("0", "", null). They must never enter a
// bulk selection: moving one aborts the batch and deleting one targets a
// nonexistent record.
export const isRealDocumentId = (value: unknown): boolean => {
  const normalized = normalize(value).toLowerCase();
  return (
    normalized !== "" &&
    normalized !== "0" &&
    normalized !== "null" &&
    normalized !== "undefined"
  );
};

const isSameDestination = (
  document: MovableDocument,
  destination: DocumentMoveDestination
) => {
  if (document.source.sectionKey !== destination.sectionKey) return false;
  if (destination.kind === "source") {
    return normalize(document.source.sourceId) === normalize(destination.sourceId);
  }
  if (destination.kind === "id-type") {
    if (document.source.slot && destination.idSlot) {
      return document.source.slot === destination.idSlot;
    }
    return false;
  }
  return destination.kind === "section";
};

const countDocumentsAlreadyInDestination = (
  documents: MovableDocument[],
  destination: DocumentMoveDestination
) => documents.filter((document) => isSameDestination(document, destination)).length;

export const getMovePayloadDocumentId = (destination: DocumentMoveDestination) => {
  if (destination.sectionKey === "id" && destination.kind === "id-type") {
    return destination.documentId ?? destination.idType;
  }
  if (destination.sectionKey === "ssn") return 4;
  if (destination.sectionKey === "paystub") return 3;
  if (destination.sectionKey === "bank") return 6;
  if (destination.sectionKey === "other_income") return 7;
  if (destination.sectionKey === "other") return 5;
  return destination.documentId ?? null;
};

export const getMovePayloadSourceId = (destination: DocumentMoveDestination) => {
  if (destination.kind === "source") return destination.sourceId;
  return destination.sourceId ?? 1;
};

export const validateDocumentMove = (
  documents: MovableDocument[],
  destination: DocumentMoveDestination
): DocumentMoveValidationResult => {
  if (!documents.length) {
    return { valid: false, message: "Select one or more documents first." };
  }

  if (destination.disabledReason) {
    return { valid: false, message: destination.disabledReason };
  }

  if (destination.kind === "trash") {
    return { valid: true };
  }

  if (
    SOURCE_REQUIRED_SECTIONS.has(destination.sectionKey) &&
    destination.kind !== "source"
  ) {
    return {
      valid: false,
      message: `${destination.label} requires selecting a source.`,
    };
  }

  const currentDocumentCount = Number(destination.currentDocumentCount || 0);
  const selectedAlreadyThere = countDocumentsAlreadyInDestination(
    documents,
    destination
  );
  const finalDocumentCount =
    currentDocumentCount - selectedAlreadyThere + documents.length;

  if (selectedAlreadyThere === documents.length) {
    return {
      valid: false,
      message: "The selected documents are already in this destination.",
    };
  }

  if (destination.sectionKey === "id") {
    if (documents.length !== 1) {
      return {
        valid: false,
        message:
          "Only one document can be moved to the ID section at a time. Clear this selection and move a single document.",
      };
    }
    if (destination.kind !== "id-type") {
      return {
        valid: false,
        message: "Choose an ID Type target before moving this document.",
      };
    }

    const targetIdType = normalize(destination.idType);
    const lockedIdType = normalize(destination.idSlotState?.idType);
    if (lockedIdType && targetIdType && lockedIdType !== targetIdType) {
      return {
        valid: false,
        message: "This ID section already contains a different ID Type.",
      };
    }

    if (TWO_SIDED_ID_TYPES.has(targetIdType)) {
      if (
        destination.idSlot === "front" &&
        destination.idSlotState?.frontDocumentId
      ) {
        return { valid: false, message: "The Front ID slot is already full." };
      }
      if (
        destination.idSlot === "back" &&
        destination.idSlotState?.backDocumentId
      ) {
        return { valid: false, message: "The Back ID slot is already full." };
      }
      if (finalDocumentCount > 2) {
        return {
          valid: false,
          message: "Driver's License and State ID can contain only Front and Back documents.",
        };
      }
      return { valid: true };
    }

    if (FRONT_ONLY_ID_TYPES.has(targetIdType) && finalDocumentCount > 1) {
      return {
        valid: false,
        message: "This ID Type can contain only one document.",
      };
    }

    return { valid: true };
  }

  if (destination.sectionKey === "ssn" && finalDocumentCount > 1) {
    return {
      valid: false,
      message: "Social Security Number can contain only one document.",
    };
  }

  if (
    (destination.sectionKey === "paystub" ||
      destination.sectionKey === "bank" ||
      destination.sectionKey === "other_income" ||
      destination.sectionKey === "other") &&
    finalDocumentCount > 8
  ) {
    return {
      valid: false,
      message: `${destination.label} can contain at most 8 documents.`,
    };
  }

  if (destination.sectionKey === "paystub") {
    const selectedOfferLetters = documents.filter(
      (document) => document.isOfferLetter
    ).length;
    if (Number(destination.currentOfferLetterCount || 0) + selectedOfferLetters > 1) {
      return {
        valid: false,
        message: "An employment income source can contain only one Offer Letter.",
      };
    }
  }

  return { valid: true };
};
